import { useState } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Edit, Trash2, X, Search, Phone, Mail, User } from "lucide-react";
import { toast } from "sonner";
import { API, statusColors } from "./utils";

function VendorsContent({ projectId, vendors, onRefresh, canWrite, token }) {
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState({
    vendor_name: "",
    work_category: "",
    scope_of_work: "",
    contract_value: 0,
    contact_person: "",
    contact_phone: "",
    contact_email: "",
    status: "Active"
  });

  // Filter vendors based on search term
  const filteredVendors = vendors.filter(v => {
    const search = searchTerm.toLowerCase();
    return (
      (v.vendor_name || "").toLowerCase().includes(search) ||
      (v.work_category || "").toLowerCase().includes(search) ||
      (v.contact_person || "").toLowerCase().includes(search) ||
      (v.contact_phone || "").includes(search) ||
      (v.contact_email || "").toLowerCase().includes(search) ||
      (v.status || "").toLowerCase().includes(search)
    );
  });

  const resetForm = () => {
    setFormData({
      vendor_name: "",
      work_category: "",
      scope_of_work: "",
      contract_value: 0,
      contact_person: "",
      contact_phone: "",
      contact_email: "",
      status: "Active"
    });
    setEditingVendor(null);
    setShowForm(false);
  };

  const handleEditVendor = (vendor) => {
    setFormData({
      vendor_name: vendor.vendor_name || "",
      work_category: vendor.work_category || "",
      scope_of_work: vendor.scope_of_work || "",
      contract_value: vendor.contract_value || 0,
      contact_person: vendor.contact_person || "",
      contact_phone: vendor.contact_phone || "",
      contact_email: vendor.contact_email || "",
      status: vendor.status || "Active"
    });
    setEditingVendor(vendor);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.vendor_name) {
      toast.error("Vendor name is required");
      return;
    }
    setSubmitting(true);
    try {
      if (editingVendor) {
        await axios.put(`${API}/projects/${projectId}/vendors/${editingVendor.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Vendor updated successfully");
      } else {
        await axios.post(`${API}/projects/${projectId}/vendors`, formData, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
        });
        toast.success("Vendor added successfully");
      }
      resetForm();
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save vendor");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteVendor = async (vendorId) => {
    if (!window.confirm("Delete this vendor?")) return;
    try {
      await axios.delete(`${API}/projects/${projectId}/vendors/${vendorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Vendor deleted");
      onRefresh();
    } catch (error) {
      toast.error("Failed to delete vendor");
    }
  };

  return (
    <div className="space-y-4">
      {/* Search and Add Button Row */}
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search vendors by name, category, contact, phone, email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="project-vendors-search"
          />
        </div>
        {canWrite && !showForm && (
          <Button size="sm" onClick={() => setShowForm(true)} className="bg-brillien-600 hover:bg-brillien-700" data-testid="add-vendor-btn">
            <Plus className="h-4 w-4 mr-1" />Add Vendor
          </Button>
        )}
      </div>

      {showForm && (
        <Card className="p-4 border-brillien-200 bg-brillien-50/50">
          <div className="flex justify-between items-center mb-4">
            <h4 className="font-semibold">{editingVendor ? "Edit Vendor" : "Add New Vendor"}</h4>
            <Button variant="ghost" size="sm" onClick={resetForm}><X className="h-4 w-4" /></Button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Vendor Name *</Label>
                <Input value={formData.vendor_name} onChange={(e) => setFormData({...formData, vendor_name: e.target.value})} required />
              </div>
              <div>
                <Label>Work Category *</Label>
                <Select value={formData.work_category} onValueChange={(v) => setFormData({...formData, work_category: v})}>
                  <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Civil">Civil</SelectItem>
                    <SelectItem value="Electrical">Electrical</SelectItem>
                    <SelectItem value="Plumbing">Plumbing</SelectItem>
                    <SelectItem value="Carpentry">Carpentry</SelectItem>
                    <SelectItem value="Painting">Painting</SelectItem>
                    <SelectItem value="HVAC">HVAC</SelectItem>
                    <SelectItem value="Flooring">Flooring</SelectItem>
                    <SelectItem value="False Ceiling">False Ceiling</SelectItem>
                    <SelectItem value="Glass & Aluminum">Glass & Aluminum</SelectItem>
                    <SelectItem value="Furniture">Furniture</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Scope of Work</Label>
              <Textarea value={formData.scope_of_work} onChange={(e) => setFormData({...formData, scope_of_work: e.target.value})} rows={2} />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Contract Value (Rs.)</Label>
                <Input type="number" value={formData.contract_value} onChange={(e) => setFormData({...formData, contract_value: parseFloat(e.target.value) || 0})} />
              </div>
              <div>
                <Label>Contact Person</Label>
                <Input value={formData.contact_person} onChange={(e) => setFormData({...formData, contact_person: e.target.value})} />
              </div>
              <div>
                <Label>Contact Phone</Label>
                <Input value={formData.contact_phone} onChange={(e) => setFormData({...formData, contact_phone: e.target.value})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Contact Email</Label>
                <Input type="email" value={formData.contact_email} onChange={(e) => setFormData({...formData, contact_email: e.target.value})} />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="On Hold">On Hold</SelectItem>
                    <SelectItem value="Completed">Completed</SelectItem>
                    <SelectItem value="Terminated">Terminated</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={submitting} className="bg-brillien-600 hover:bg-brillien-700">
                {submitting ? "Saving..." : editingVendor ? "Update Vendor" : "Add Vendor"}
              </Button>
              <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
            </div>
          </form>
        </Card>
      )}

      {filteredVendors.length === 0 && !showForm ? (
        <p className="text-slate-500 text-center py-8">{searchTerm ? "No vendors match your search" : "No vendors assigned"}</p>
      ) : !showForm && (
        <div className="border rounded-lg overflow-hidden dark:border-slate-700">
          <table className="w-full">
            <thead className="bg-slate-50 dark:bg-slate-800">
              <tr>
                <th className="text-left p-3 text-sm font-medium text-slate-700 dark:text-slate-300">Vendor Name</th>
                <th className="text-left p-3 text-sm font-medium text-slate-700 dark:text-slate-300">Category</th>
                <th className="text-left p-3 text-sm font-medium text-slate-700 dark:text-slate-300">Contact Details</th>
                <th className="text-right p-3 text-sm font-medium text-slate-700 dark:text-slate-300">Contract Value</th>
                <th className="text-center p-3 text-sm font-medium text-slate-700 dark:text-slate-300">Status</th>
                <th className="text-right p-3 text-sm font-medium text-slate-700 dark:text-slate-300">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
              {filteredVendors.map(v => (
                <tr key={v.id} className="hover:bg-slate-50 dark:hover:bg-slate-800" data-testid={`vendor-row-${v.id}`}>
                  <td className="p-3">
                    <div>
                      <p className="font-medium text-slate-900 dark:text-slate-100">{v.vendor_name}</p>
                      {v.scope_of_work && <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-1">{v.scope_of_work}</p>}
                    </div>
                  </td>
                  <td className="p-3 text-sm text-slate-600 dark:text-slate-400">{v.work_category || "-"}</td>
                  <td className="p-3">
                    <div className="text-sm space-y-1">
                      {v.contact_person && (
                        <div className="flex items-center gap-1.5 text-slate-900 dark:text-slate-100">
                          <User className="h-3.5 w-3.5 text-slate-400" />
                          <span>{v.contact_person}</span>
                        </div>
                      )}
                      {v.contact_phone && (
                        <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-400">
                          <Phone className="h-3.5 w-3.5 text-slate-400" />
                          <span>{v.contact_phone}</span>
                        </div>
                      )}
                      {v.contact_email && (
                        <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-400">
                          <Mail className="h-3.5 w-3.5 text-slate-400" />
                          <span className="truncate max-w-[180px]">{v.contact_email}</span>
                        </div>
                      )}
                      {!v.contact_person && !v.contact_phone && !v.contact_email && (
                        <span className="text-slate-400">-</span>
                      )}
                    </div>
                  </td>
                  <td className="p-3 text-right text-sm font-medium text-slate-900 dark:text-slate-100">
                    {v.contract_value > 0 ? `Rs. ${v.contract_value.toLocaleString()}` : "-"}
                  </td>
                  <td className="p-3 text-center">
                    <Badge className={statusColors[v.status]}>{v.status}</Badge>
                  </td>
                  <td className="p-3 text-right">
                    {canWrite && (
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleEditVendor(v)} className="text-blue-600 h-8 w-8 p-0"><Edit className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteVendor(v.id)} className="text-red-600 h-8 w-8 p-0"><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============ MATERIALS CONTENT ============

export { VendorsContent };
